// Canonical Malaysian address subdivisions (16 states + federal territories).
// Stored values match the official UN/LOCODE subdivision names.
// Single source of truth for address-form dropdowns. Service-area logic
// (LocationSelector) uses a different ID-based scheme and is intentionally separate.

export interface AddressStateInfo {
  name: string
  aliases: string[]
  cities: string[]
}

export const ADDRESS_STATES: AddressStateInfo[] = [
  {
    name: 'Johor',
    aliases: ['JHR', 'JOH', 'Johore'],
    cities: [
      'Johor Bahru', 'Batu Pahat', 'Kluang', 'Muar', 'Segamat',
      'Kulai', 'Pontian', 'Kota Tinggi', 'Mersing', 'Tangkak',
      'Iskandar Puteri',
    ],
  },
  {
    name: 'Kedah',
    aliases: ['KDH', 'KED'],
    cities: [
      'Alor Setar', 'Sungai Petani', 'Kulim', 'Langkawi', 'Kuala Kedah',
      'Yan', 'Sik', 'Baling', 'Padang Terap', 'Pokok Sena',
    ],
  },
  {
    name: 'Kelantan',
    aliases: ['KTN', 'KEL'],
    cities: [
      'Kota Bharu', 'Pasir Mas', 'Tumpat', 'Bachok', 'Kuala Krai',
      'Machang', 'Tanah Merah', 'Pasir Puteh', 'Gua Musang', 'Jeli',
    ],
  },
  {
    name: 'Melaka',
    aliases: ['MEL', 'Malacca'],
    cities: [
      'Melaka City', 'Alor Gajah', 'Jasin', 'Masjid Tanah', 'Merlimau',
      'Tampin', 'Sungai Udang', 'Batu Berendam', 'Bukit Beruang',
    ],
  },
  {
    name: 'Negeri Sembilan',
    aliases: ['NS', 'NSN', 'N. Sembilan'],
    cities: [
      'Seremban', 'Port Dickson', 'Nilai', 'Bahau', 'Kuala Pilah',
      'Rembau', 'Tampin', 'Jempol', 'Gemenceh',
    ],
  },
  {
    name: 'Pahang',
    aliases: ['PAH', 'PHG'],
    cities: [
      'Kuantan', 'Temerloh', 'Bentong', 'Jerantut', 'Mentakab',
      'Raub', 'Lipis', 'Maran', 'Pekan', 'Bera',
    ],
  },
  {
    name: 'Pulau Pinang',
    aliases: ['PEN', 'PNG', 'Penang'],
    cities: [
      'George Town', 'Butterworth', 'Bukit Mertajam', 'Seberang Perai',
      'Balik Pulau', 'Nibong Tebal', 'Tanjung Bungah', 'Bayan Lepas',
    ],
  },
  {
    name: 'Perak',
    aliases: ['PER', 'PRK'],
    cities: [
      'Ipoh', 'Taiping', 'Teluk Intan', 'Sitiawan', 'Kampar',
      'Kuala Kangsar', 'Parit Buntar', 'Batu Gajah', 'Tanjung Malim',
    ],
  },
  {
    name: 'Perlis',
    aliases: ['PERL', 'PLS'],
    cities: [
      'Kangar', 'Arau', 'Kuala Perlis', 'Padang Besar', 'Simpang Empat',
    ],
  },
  {
    name: 'Sabah',
    aliases: ['SBH', 'SAH'],
    cities: [
      'Kota Kinabalu', 'Sandakan', 'Tawau', 'Lahad Datu', 'Keningau',
      'Kota Belud', 'Kinarut', 'Beaufort', 'Kunak', 'Papar',
    ],
  },
  {
    name: 'Sarawak',
    aliases: ['SRW', 'SAW'],
    cities: [
      'Kuching', 'Miri', 'Sibu', 'Bintulu', 'Sarikei',
      'Kapit', 'Limbang', 'Sri Aman', 'Betong', 'Mukah',
    ],
  },
  {
    name: 'Selangor',
    aliases: ['SEL', 'SGR'],
    cities: [
      'Shah Alam', 'Petaling Jaya', 'Subang Jaya', 'Klang', 'Kajang',
      'Bangi', 'Rawang', 'Ampang', 'Hulu Selangor', 'Sepang',
    ],
  },
  {
    name: 'Terengganu',
    aliases: ['TRG', 'TERG', 'Trengganu'],
    cities: [
      'Kuala Terengganu', 'Kemaman', 'Dungun', 'Marang', 'Besut',
      'Hulu Terengganu', 'Kuala Nerus', 'Setiu',
    ],
  },
  {
    name: 'WP Kuala Lumpur',
    aliases: ['KL', 'WPKL', 'Kuala Lumpur', 'W.P. Kuala Lumpur', 'Wilayah Persekutuan Kuala Lumpur'],
    cities: [
      'Kuala Lumpur City', 'Bangsar', 'Petaling Jaya', 'Subang Jaya',
      'Ampang', 'Cheras', 'Kepong', 'Selayang', 'Gombak', 'Wangsa Maju',
    ],
  },
  {
    name: 'WP Labuan',
    aliases: ['LAB', 'WPLB', 'Labuan', 'W.P. Labuan'],
    cities: [
      'Victoria (Labuan)', 'Batu Manikar', 'Layang-Layang', 'Rampayan',
      'Sang Kancil', 'Pohon Batu',
    ],
  },
  {
    name: 'WP Putrajaya',
    aliases: ['PUTRA', 'WPPT', 'Putrajaya', 'W.P. Putrajaya'],
    cities: [
      'Putrajaya City', 'Precinct 1', 'Precinct 8', 'Precinct 9',
      'Precinct 11', 'Precinct 12', 'Precinct 13', 'Precinct 16', 'Precinct 18',
    ],
  },
]

export const ADDRESS_STATE_NAMES: string[] = ADDRESS_STATES.map((s) => s.name)

export const getCitiesForStateName = (name: string): string[] =>
  ADDRESS_STATES.find((s) => s.name === name)?.cities ?? []
